import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Building2, Calendar, ChevronRight, Loader2, MessageSquare,
  Settings, Plus, CalendarDays, Activity, Users, Upload, ListChecks, ChevronDown, Paperclip,
  Mail, Phone, Linkedin
} from 'lucide-react';
import api from '../utils/api';
import { Button } from './ui/button';
import { getCommitteeColor } from '../utils/committeeColors';
import DocumentsTab from './BoardDocuments';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import { Badge } from './ui/badge';

const CommitteeCalendarTab = ({ committeeId, onViewMeeting }) => {
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCalendarData = async () => {
    if (!committeeId) return;
    setLoading(true);
    try {
      const [urlRes, meetingsRes] = await Promise.all([
        api.get(`/governance/committees/${committeeId}/calendar/subscription-url`),
        api.get(`/governance/committees/${committeeId}/meetings`, {
          params: { upcoming_only: false, limit: 100, offset: 0 }
        })
      ]);

      setSubscriptionUrl(urlRes.data?.url || '');
      setTokenInfo({
        expiresAt: urlRes.data?.expires_at,
        daysUntilExpiry: urlRes.data?.days_until_expiry,
      });

      const normalizedMeetings = (meetingsRes.data?.meetings || []).map((meeting) => ({
        ...meeting,
        id: meeting.id || meeting.meeting_id,
      }));
      setMeetings(normalizedMeetings);
    } catch (err) {
      console.error('Failed to fetch committee calendar data:', err);
      toast.error('Failed to load committee calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [committeeId]);

  const copySubscriptionUrl = () => {
    if (!subscriptionUrl) return;
    navigator.clipboard.writeText(subscriptionUrl);
    toast.success('Calendar URL copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const isExpiringSoon = tokenInfo?.daysUntilExpiry <= 30;
  const isExpired = tokenInfo?.daysUntilExpiry <= 0;

  const meetingsByMonth = meetings.reduce((acc, meeting) => {
    if (!meeting.scheduled_start) return acc;
    const date = new Date(meeting.scheduled_start);
    const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(meeting);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <CalendarDays className="h-6 w-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Subscribe to Committee Calendar</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add committee meetings to your calendar app (Google Calendar, Outlook, Apple Calendar).
            </p>

            {isExpired && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-700">Calendar subscription expired</p>
                  <p className="text-xs text-red-600">Please refresh your subscription URL.</p>
                </div>
              </div>
            )}
            {isExpiringSoon && !isExpired && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-700">
                    Subscription expires in {tokenInfo.daysUntilExpiry} days
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <Input
                value={subscriptionUrl}
                readOnly
                className="flex-1 bg-gray-50 text-sm"
              />
              <Button variant="outline" onClick={copySubscriptionUrl}>
                Copy URL
              </Button>
              <Button variant="outline" onClick={() => openCommitteeCalendarSubscription(committeeId)}>
                Open
              </Button>
            </div>

            <div className="text-xs text-gray-500">
              {tokenInfo?.expiresAt && !isExpired && (
                <>Valid until {new Date(tokenInfo.expiresAt).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })}</>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">All Committee Meetings</h3>
        </div>

        {Object.keys(meetingsByMonth).length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No meetings scheduled</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {Object.entries(meetingsByMonth).map(([month, monthMeetings]) => (
              <div key={month}>
                <div className="px-6 py-3 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700">{month}</h4>
                </div>
                {monthMeetings.map((meeting) => {
                  const date = new Date(meeting.scheduled_start);
                  return (
                    <div
                      key={meeting.id}
                      className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onViewMeeting(meeting.id)}
                    >
                      <div className="text-center min-w-[50px]">
                        <p className="text-2xl font-bold text-purple-600">{date.getDate()}</p>
                        <p className="text-xs text-gray-500 uppercase">{date.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{meeting.title}</p>
                        <p className="text-sm text-gray-500">
                          {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {meeting.location && ` • ${meeting.location}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={meeting.status === 'cancelled' ? 'destructive' : 'secondary'}>
                          {meeting.status}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewMeeting(meeting.id);
                          }}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const formatIcsDate = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

const escapeIcsText = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
};

const buildMeetingIcs = (meeting) => {
  const start = new Date(meeting.scheduled_start);
  const end = meeting.scheduled_end ? new Date(meeting.scheduled_end) : new Date(start.getTime() + 60 * 60 * 1000);
  const locationParts = [];
  if (meeting.location) locationParts.push(meeting.location);
  if (meeting.video_link) locationParts.push(`Video: ${meeting.video_link}`);
  const location = locationParts.join(' | ');
  const description = meeting.description || '';
  const uid = `committee-meeting-${meeting.id}@athena`;
  const dtstamp = formatIcsDate(new Date());

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Athena//Committee Meetings//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${formatIcsDate(start)}`,
    `DTEND:${formatIcsDate(end)}`,
    `SUMMARY:${escapeIcsText(meeting.title)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    `LOCATION:${escapeIcsText(location)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

const downloadMeetingIcs = (meeting) => {
  const ics = buildMeetingIcs(meeting);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${meeting.title || 'meeting'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const openCommitteeCalendarSubscription = async (committeeId) => {
  try {
    const res = await api.get(`/governance/committees/${committeeId}/calendar/subscription-url`);
    const url = res?.data?.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    toast.error('Failed to load committee calendar subscription');
  } catch (err) {
    toast.error('Failed to load committee calendar subscription');
  }
};

const CommitteeDashboard = () => {
  const navigate = useNavigate();
  const { committeeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [committees, setCommittees] = useState([]);
  const [access, setAccess] = useState({ user_role: 'viewer', committees_enabled: false });
  const [accessError, setAccessError] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [meetings, setMeetings] = useState([]);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState({});
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showAgendaDialog, setShowAgendaDialog] = useState(false);
  const [showAgendaReview, setShowAgendaReview] = useState(false);
  const [agendaReviewMeetingId, setAgendaReviewMeetingId] = useState('');
  const [selectedMeetingId, setSelectedMeetingId] = useState('');

  useEffect(() => {
    const fetchCommittees = async () => {
      setLoading(true);
      try {
        const response = await api.get('/governance/access');
        setAccess(response.data || { user_role: 'viewer', committees_enabled: false });
        setCommittees(response.data?.committees || []);
        setAccessError(!(response.data?.committees_enabled));
      } catch (err) {
        console.error('Failed to load committees:', err);
        setCommittees([]);
        setAccessError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCommittees();
    window.scrollTo(0, 0);
  }, []);

  const committee = useMemo(
    () => committees.find((item) => item.id === committeeId) || null,
    [committees, committeeId]
  );
  const committeeColor = useMemo(
    () => getCommitteeColor(committee?.name || '', committee?.color_hex),
    [committee?.name, committee?.color_hex]
  );

  const canManageMeetings = useMemo(() => {
    if (!committee) return false;
    return ['owner', 'admin'].includes(access.user_role) || committee.role === 'chair';
  }, [access.user_role, committee]);

  const canManageDocuments = useMemo(() => {
    if (!committee) return false;
    if (['owner', 'admin', 'board_member'].includes(access.user_role)) return true;
    return ['chair', 'member'].includes(committee.role);
  }, [access.user_role, committee]);

  const governanceBackPath = useMemo(() => (
    access?.has_board_access ? '/governance' : '/dashboard'
  ), [access?.has_board_access]);

  useEffect(() => {
    if (!committee) return;
    const fetchMeetings = async () => {
      setMeetingsLoading(true);
      try {
        const response = await api.get(`/governance/committees/${committee.id}/meetings`, {
          params: { upcoming_only: true, limit: 50, offset: 0 }
        });
        const normalizedMeetings = (response.data?.meetings || []).map((m) => ({
          ...m,
          id: m.id || m.meeting_id
        }));
        setMeetings(normalizedMeetings);
      } catch (err) {
        console.error('Failed to load committee meetings:', err);
        setMeetings([]);
      } finally {
        setMeetingsLoading(false);
      }
    };
    fetchMeetings();
  }, [committee]);

  useEffect(() => {
    if (!committee) return;
    const fetchMembers = async () => {
      setMembersLoading(true);
      try {
        const response = await api.get(`/governance/committees/${committee.id}/members`);
        setCommitteeMembers(response.data?.members || []);
      } catch (err) {
        console.error('Failed to load committee members:', err);
        setCommitteeMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    fetchMembers();
  }, [committee]);

  const handleMeetingCreated = async () => {
    setShowCreateMeeting(false);
    if (!committee) return;
    setMeetingsLoading(true);
    try {
      const response = await api.get(`/governance/committees/${committee.id}/meetings`, {
        params: { upcoming_only: true, limit: 50, offset: 0 }
      });
      const normalizedMeetings = (response.data?.meetings || []).map((m) => ({
        ...m,
        id: m.id || m.meeting_id
      }));
      setMeetings(normalizedMeetings);
      toast.success('Meeting scheduled successfully');
    } finally {
      setMeetingsLoading(false);
    }
  };

  const nextMeeting = meetings.length > 0 ? meetings.reduce((nearest, m) => {
    const mDate = new Date(m.scheduled_start);
    const nDate = nearest ? new Date(nearest.scheduled_start) : null;
    if (!nDate || mDate < nDate) return m;
    return nearest;
  }, null) : null;

  const upcomingMeetings = meetings.filter(m => m.id !== nextMeeting?.id);

  const handleCreateAgenda = (meetingId) => {
    if (!meetingId) {
      toast.error('Meeting not found');
      return;
    }
    setSelectedMeetingId(meetingId);
    setShowAgendaDialog(true);
  };

  const handleViewAgenda = (meetingId) => {
    if (!meetingId) {
      toast.error('Meeting not found');
      return;
    }
    setAgendaReviewMeetingId(meetingId);
    setShowAgendaReview(true);
  };

  const openDirectMessage = async (member) => {
    if (!member?.user_id) return;
    setMessageLoading(prev => ({ ...prev, [member.user_id]: true }));
    try {
      const response = await api.post('/conversations', {
        type: 'direct',
        participant_ids: [member.user_id]
      });
      const conversationId = response.data?.id;
      if (conversationId) {
        sessionStorage.setItem('pendingConversation', conversationId);
        window.dispatchEvent(new CustomEvent('openConversation', { detail: conversationId }));
      }
      navigate('/organization/messaging');
    } catch (err) {
      console.error('Failed to open direct message:', err);
      toast.error(err.response?.data?.detail || 'Failed to open message');
    } finally {
      setMessageLoading(prev => ({ ...prev, [member.user_id]: false }));
    }
  };

  const handleViewMeeting = (meetingId) => {
    if (!meetingId) {
      toast.error('Meeting not found');
      return;
    }
    navigate(`/governance/committees/${committeeId}/meetings/${meetingId}`);
  };

  const handleOpenDocuments = () => {
    setActiveTab('documents');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Committees unavailable</h2>
          <p className="text-gray-500 mb-4">Committee access is disabled or unavailable.</p>
          <Button onClick={() => navigate(governanceBackPath)}>Back to Governance</Button>
        </div>
      </div>
    );
  }

  if (!committee) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Committee not found</h2>
          <p className="text-gray-500 mb-4">We could not locate this committee.</p>
          <Button onClick={() => navigate(governanceBackPath)}>Back to Governance</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex-1 w-full" data-testid="committee-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(governanceBackPath)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Governance
              </Button>
              <div className="border-l border-gray-200 h-6" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: committeeColor.soft }}>
                  <Building2 className="h-5 w-5" style={{ color: committeeColor.accent }} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{committee.name}</h1>
                  <p className="text-sm text-gray-500">{committee.description || 'Committee workspace'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(`/governance/committees/${committeeId}/settings`)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button disabled={!canManageMeetings}>
                    <Plus className="h-4 w-4 mr-2" />
                    New
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => setShowCreateMeeting(true)}>
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Schedule Meeting
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenDocuments}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleCreateAgenda(nextMeeting?.id || nextMeeting?.meeting_id || '')}>
                    <ListChecks className="h-4 w-4 mr-2" />
                    Create Agenda
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between border-b border-gray-200">
                <TabsList className="bg-white border border-gray-200 p-1">
                  <TabsTrigger value="overview" className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Documents
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Calendar
                  </TabsTrigger>
                  <TabsTrigger value="members" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Members
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="py-6">
                <TabsContent value="overview">
                  <CommitteeOverview
                    committee={committee}
                    committeeColor={committeeColor}
                    nextMeeting={nextMeeting}
                    upcomingMeetings={upcomingMeetings}
                    canManageMeetings={canManageMeetings}
                    meetingsLoading={meetingsLoading}
                    onSchedule={() => setShowCreateMeeting(true)}
                    onViewAgenda={handleViewAgenda}
                    onViewMeeting={handleViewMeeting}
                  />
                </TabsContent>
                <TabsContent value="documents">
                  <DocumentsTab
                    userRole={access.user_role}
                    currentOrganizationId={committeeId}
                    committeeId={committeeId}
                    canManageDocuments={canManageDocuments}
                  />
                </TabsContent>
                <TabsContent value="calendar">
                  <CommitteeCalendarTab committeeId={committeeId} onViewMeeting={handleViewMeeting} />
                </TabsContent>
                <TabsContent value="members">
                  <CommitteeMembersTab
                    members={committeeMembers}
                    loading={membersLoading}
                    onMessage={openDirectMessage}
                    messageLoading={messageLoading}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      <CreateCommitteeMeetingDialog
        open={showCreateMeeting}
        onOpenChange={setShowCreateMeeting}
        committeeId={committeeId}
        onSuccess={handleMeetingCreated}
      />

      <CreateCommitteeAgendaDialog
        open={showAgendaDialog}
        onOpenChange={setShowAgendaDialog}
        committeeId={committeeId}
        committeeName={committee?.name || ''}
        committeeColor={committeeColor}
        meetings={meetings}
        preSelectedMeetingId={selectedMeetingId}
        onSuccess={() => {
          setShowAgendaDialog(false);
          toast.success('Agenda created');
        }}
      />

      <CommitteeAgendaReviewDialog
        open={showAgendaReview}
        onOpenChange={setShowAgendaReview}
        committeeId={committeeId}
        meetingId={agendaReviewMeetingId}
        committeeColor={committeeColor}
        onCreateAgenda={(meetingId) => handleCreateAgenda(meetingId)}
      />
    </div>
  );
};

const CommitteeOverview = ({ committee, committeeColor, nextMeeting, upcomingMeetings, canManageMeetings, meetingsLoading, onSchedule, onViewAgenda, onViewMeeting }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <NextMeetingCard
          committeeColor={committeeColor}
          meeting={nextMeeting}
          canManageMeetings={canManageMeetings}
          onSchedule={onSchedule}
          loading={meetingsLoading}
          onViewAgenda={onViewAgenda}
          onViewMeeting={onViewMeeting}
          committeeId={committee?.id}
        />
        <ActivityFeed committeeId={committee?.id} />
      </div>
      <div className="space-y-4">
        <UpcomingMeetingsList
          meetings={upcomingMeetings}
          loading={meetingsLoading}
          onSchedule={onSchedule}
          canManageMeetings={canManageMeetings}
        />
        <CommitteeChat committee={committee} committeeId={committee?.id} />
      </div>
    </div>
  );
};

const CommitteeMembersTab = ({ members, loading, onMessage, messageLoading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading committee members...
      </div>
    );
  }

  if (!members || members.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
        No committee members yet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {members.map((member) => {
        const initials = member.name
          ? member.name.split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase()
          : 'U';
        const roleLabel = member.role ? member.role.replace(/_/g, ' ') : '';
        const titleLine = [member.job_title, member.company].filter(Boolean).join(' • ');
        return (
          <div
            key={member.user_id}
            className="group bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg hover:border-purple-200 hover:bg-purple-50/60"
          >
            <div className="mx-auto h-20 w-20 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
              {member.profile_image_url ? (
                <img
                  src={member.profile_image_url}
                  alt={member.name || 'Member'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-purple-700 font-semibold text-lg">{initials}</span>
              )}
            </div>

            <div className="mt-4">
              <h3 className="font-semibold text-gray-900">{member.name || 'Committee Member'}</h3>
              {titleLine && (
                <p className="text-sm text-gray-500 mt-1">{titleLine}</p>
              )}
              {!titleLine && roleLabel && member.role !== 'chair' && (
                <p className="text-sm text-gray-500 mt-1 capitalize">{roleLabel}</p>
              )}
              {member.role === 'chair' && (
                <div className="mt-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Chair</span>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-3 text-sm text-gray-600 text-left">
              {member.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                  <a href={`mailto:${member.email}`} className="text-gray-700 group-hover:text-purple-700 hover:underline truncate">
                    {member.email}
                  </a>
                </div>
              )}
              {(member.phone || member.phone_number) && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                  <span className="text-gray-700 group-hover:text-purple-700">{member.phone || member.phone_number}</span>
                </div>
              )}
              {member.linkedin && (
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 group-hover:text-purple-700 hover:underline truncate"
                  >
                    {member.linkedin}
                  </a>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onMessage(member)}
                disabled={messageLoading?.[member.user_id]}
              >
                {messageLoading?.[member.user_id] ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <MessageSquare className="h-4 w-4 mr-2" />
                )}
                Message
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const NextMeetingCard = ({ committeeColor, meeting, canManageMeetings, onSchedule, loading, onViewAgenda, onViewMeeting, committeeId }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4" style={{ background: committeeColor?.gradient }}>
          <h2 className="text-white font-semibold text-lg">Next Meeting</h2>
        </div>
        <div className="p-8 text-center text-gray-500">Loading meetings...</div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4" style={{ background: committeeColor?.gradient }}>
          <h2 className="text-white font-semibold text-lg">Next Meeting</h2>
        </div>
        <div className="p-8 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No committee meetings scheduled yet</p>
          {canManageMeetings && (
            <Button className="border-0" style={{ background: committeeColor?.gradient }} onClick={onSchedule}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Meeting
            </Button>
          )}
        </div>
      </div>
    );
  }

  const meetingDate = new Date(meeting.scheduled_start);
  const formatDate = (date) => date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const formatTime = (date) => date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  const meetingHref = meeting?.id && committeeId
    ? `/governance/committees/${committeeId}/meetings/${meeting.id}`
    : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4" style={{ background: committeeColor?.gradient }}>
        <h2 className="text-white font-semibold text-lg">Next Meeting</h2>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{meeting.title}</h3>
        <p className="text-gray-600 mb-1">{formatDate(meetingDate)} | {formatTime(meetingDate)}</p>
        <p className="text-gray-500 text-sm mb-4">
          {meeting.location || 'Location TBD'}
        </p>
        <div className="flex flex-wrap gap-2">
          {meetingHref ? (
            <Button variant="outline" size="sm" asChild>
              <Link to={meetingHref}>View Meeting</Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => onViewMeeting?.(meeting.id)}
            >
              View Meeting
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => onViewAgenda(meeting.id)}
          >
            View Agenda
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadMeetingIcs(meeting)}>Add to Calendar</Button>
          <Button variant="outline" size="sm" onClick={() => openCommitteeCalendarSubscription(committeeId)}>
            Subscribe
          </Button>
        </div>
      </div>
    </div>
  );
};

const UpcomingMeetingsList = ({ meetings, loading, onSchedule, canManageMeetings }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Upcoming Meetings</h3>
        <button type="button" className="text-sm text-purple-600 hover:text-purple-700" disabled={!canManageMeetings} onClick={onSchedule}>
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {loading ? (
        <div className="p-6 text-center text-gray-500">Loading meetings...</div>
      ) : meetings.length === 0 ? (
        <div className="p-6 text-center text-gray-500">
          <Calendar className="h-8 w-8 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No upcoming meetings</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {meetings.slice(0, 3).map((meeting) => {
            const date = new Date(meeting.scheduled_start);
            return (
              <div key={meeting.id} className="px-5 py-4">
                <p className="text-sm font-medium text-gray-900">{meeting.title}</p>
                <p className="text-xs text-gray-500">
                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} •{' '}
                  {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CommitteeChat = ({ committee, committeeId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [connected, setConnected] = useState(false);
  const [usingPolling, setUsingPolling] = useState(false);
  const messagesContainerRef = React.useRef(null);
  const wsRef = React.useRef(null);
  const pollIntervalRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  const fetchMessages = async () => {
    if (!committeeId) return;
    try {
      const response = await api.get(`/governance/committees/${committeeId}/chat`);
      setMessages(response.data.messages || []);
    } catch (err) {
      console.log('Committee chat fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    setUsingPolling(true);
    setConnected(true);
    if (!pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(fetchMessages, 3000);
    }
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token || !committeeId) {
      startPolling();
      return;
    }

    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const urlObj = new URL(apiUrl);
    const wsUrl = `${wsProtocol}://${urlObj.host}/api/governance/committees/${committeeId}/chat/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          startPolling();
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        setConnected(true);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'message') {
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.data.id)) {
              return prev;
            }
            return [...prev, data.data];
          });
        } else if (data.type === 'delete') {
          setMessages((prev) => prev.filter((m) => m.id !== data.message_id));
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        startPolling();
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (err) {
      console.warn('Committee chat WS failed, using polling:', err);
      startPolling();
    }
  };

  useEffect(() => {
    fetchMessages();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [committeeId]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !committeeId) return;
    setSending(true);
    try {
      let attachmentUrls = [];
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);
        const uploads = [];
        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append('file', file);
          uploads.push(
            api.post('/upload/message-attachment', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            }).then((res) => res.data?.url).catch(() => null)
          );
        }
        const results = await Promise.all(uploads);
        attachmentUrls = results.filter(Boolean);
      }

      const response = await api.post(`/governance/committees/${committeeId}/chat`, {
        message: newMessage.trim() || '(attachment)',
        attachments: attachmentUrls.length > 0 ? attachmentUrls : null
      });
      const sent = response.data?.message;
      if (sent) {
        setMessages((prev) => [...prev, sent]);
      }
      setNewMessage('');
      setSelectedFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
      setUploadingFiles(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const renderAttachments = (attachments, isOwn) => {
    if (!attachments || attachments.length === 0) return null;
    let normalized = attachments;
    if (typeof normalized === 'string') {
      try {
        normalized = JSON.parse(normalized);
      } catch (e) {
        normalized = [normalized];
      }
    }
    if (!Array.isArray(normalized)) return null;

    return (
      <div className="mt-2 space-y-2">
        {normalized.map((attachment, index) => {
          const url = typeof attachment === 'string'
            ? attachment
            : attachment.url || attachment.file_url || attachment.proxy_url || attachment.download_url || '';
          if (!url) {
            const name = attachment.filename || attachment.file_name || attachment.name || 'attachment';
            return (
              <div key={index} className={`text-xs ${isOwn ? 'text-purple-100' : 'text-gray-600'}`}>
                {name}
              </div>
            );
          }
          const urlPath = url.split('?')[0];
          const isImage = urlPath.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          const isVideo = urlPath.match(/\.(mp4|webm|ogg|mov)$/i);
          const fileName = (attachment.filename || attachment.file_name || attachment.name || urlPath.split('/').pop() || 'attachment');

          if (isImage) {
            return (
              <div key={index} className="max-w-xs">
                <img 
                  src={url} 
                  alt={fileName}
                  className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(url, '_blank')}
                />
              </div>
            );
          }
          if (isVideo) {
            return (
              <div key={index} className="max-w-xs">
                <video src={url} controls className="rounded-lg max-w-full h-auto" />
              </div>
            );
          }
          return (
            <div key={index} className={`flex items-center space-x-2 p-2 rounded border ${
              isOwn ? 'border-purple-400 bg-purple-500' : 'border-gray-200 bg-white'
            }`}>
              <Paperclip className={`w-4 h-4 ${isOwn ? 'text-purple-200' : 'text-gray-500'}`} />
              <a 
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm hover:underline ${isOwn ? 'text-purple-100' : 'text-blue-600'}`}
              >
                {fileName}
              </a>
            </div>
          );
        })}
      </div>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col h-[520px]">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-purple-600" />
          <h3 className="font-semibold text-gray-900">{committee.name} Chat</h3>
        </div>
        <span className="text-xs text-gray-400">
          {connected ? (usingPolling ? 'Connected (polling)' : 'Connected') : 'Offline'}
        </span>
      </div>
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center text-gray-500 text-sm">Loading chat...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm">No messages yet.</div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`flex gap-2 ${msg.isOwn ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden ${
                msg.isOwn ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {msg.user_avatar_url ? (
                  <img
                    src={msg.user_avatar_url}
                    alt={msg.user_name || 'User avatar'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  msg.user_initials || (msg.user_name ? msg.user_name.charAt(0) : 'U')
                )}
              </div>
              <div className={`flex-1 max-w-[80%] ${msg.isOwn ? 'text-right' : ''}`}>
                <div className={`inline-block rounded-lg px-3 py-2 text-sm ${msg.isOwn ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                  {!msg.isOwn && (
                    <div className="text-xs text-gray-500 mb-1">{msg.user_name}</div>
                  )}
                  {msg.message && <div>{msg.message}</div>}
                  {renderAttachments(msg.attachments, msg.isOwn)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="border-t border-gray-100 p-3 flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
        />
        <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={sending || uploadingFiles}>
          <Paperclip className="h-4 w-4" />
        </Button>
        <input
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Button size="sm" onClick={handleSend} disabled={sending || uploadingFiles || (!newMessage.trim() && selectedFiles.length === 0)}>
          Send
        </Button>
      </div>
      {selectedFiles.length > 0 && (
        <div className="border-t border-gray-100 px-3 py-2 space-y-2">
          {selectedFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs">
              <div className="flex items-center gap-2">
                <Paperclip className="h-3 w-3 text-gray-400" />
                <span className="text-gray-700 truncate">{file.name}</span>
              </div>
              <button className="text-gray-400 hover:text-red-500" onClick={() => removeFile(index)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ActivityFeed = ({ committeeId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  const getActivityIcon = (action) => {
    switch (action) {
      case 'document_uploaded':
        return <Upload className="h-4 w-4 text-purple-600" />;
      case 'document_moved':
        return <ListChecks className="h-4 w-4 text-blue-500" />;
      case 'meeting_created':
        return <Calendar className="h-4 w-4 text-green-600" />;
      case 'agenda_item_added':
        return <ListChecks className="h-4 w-4 text-indigo-500" />;
      case 'comment':
        return <MessageSquare className="h-4 w-4 text-gray-400" />;
      default:
        return <Activity className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  };

  const fetchActivity = async () => {
    if (!committeeId) return;
    try {
      setLoading(true);
      const res = await api.get(`/governance/committees/${committeeId}/activity?limit=50`);
      setActivities(res.data.activities || []);
    } catch (err) {
      console.error('Failed to load committee activity:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [committeeId]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Activity Feed</h3>
        <button
          type="button"
          className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
          onClick={fetchActivity}
          disabled={loading}
        >
          Refresh
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading activity...</div>
      ) : activities.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <Activity className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No committee activity yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3">
              <div className="mt-0.5">{getActivityIcon(activity.action)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{activity.user_name || 'Someone'}</span>{' '}
                  <span className="text-gray-600">{activity.description || activity.entity_type}</span>
                </p>
                <p className="text-xs text-gray-400">{getTimeAgo(activity.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CommitteeAgendaReviewDialog = ({
  open,
  onOpenChange,
  committeeId,
  meetingId,
  committeeColor,
  onCreateAgenda
}) => {
  const [agendaItems, setAgendaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meetingDocs, setMeetingDocs] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [pickerAgendaItemId, setPickerAgendaItemId] = useState(null);

  const fetchAgenda = async () => {
    if (!committeeId || !meetingId) return;
    try {
      setLoading(true);
      const res = await api.get(`/governance/committees/${committeeId}/meetings/${meetingId}/agenda`);
      setAgendaItems(res.data?.agenda_items || []);
    } catch (err) {
      console.error('Failed to fetch committee agenda:', err);
      setAgendaItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (query = '') => {
    if (!committeeId || !meetingId) return;
    try {
      setDocumentsLoading(true);
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      const res = await api.get(`/governance/committees/${committeeId}/meetings/${meetingId}/documents?${params.toString()}`);
      setMeetingDocs(res.data?.linked_documents || []);
      setAvailableDocuments(res.data?.available_documents || []);
    } catch (err) {
      console.error('Failed to fetch committee documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchAgenda();
    fetchDocuments();
  }, [open, committeeId, meetingId]);

  useEffect(() => {
    if (!pickerAgendaItemId) return;
    const term = documentSearch.trim();
    const timeout = setTimeout(() => {
      fetchDocuments(term);
    }, 200);
    return () => clearTimeout(timeout);
  }, [documentSearch, pickerAgendaItemId]);

  const docsByAgenda = useMemo(() => {
    const grouped = {};
    meetingDocs.forEach((doc) => {
      const key = doc.agenda_item_id || 'general';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });
    return grouped;
  }, [meetingDocs]);

  const handleAttachDocument = async (documentId, agendaItemId) => {
    try {
      await api.post(`/governance/committees/${committeeId}/meetings/${meetingId}/documents`, {
        document_id: documentId,
        agenda_item_id: agendaItemId
      });
      toast.success('Document attached');
      setPickerAgendaItemId(null);
      fetchDocuments();
    } catch (err) {
      console.error('Failed to attach document:', err);
      toast.error('Failed to attach document');
    }
  };

  const handleUnlinkDocument = async (documentId) => {
    try {
      await api.delete(`/governance/committees/${committeeId}/meetings/${meetingId}/documents/${documentId}`);
      fetchDocuments();
    } catch (err) {
      console.error('Failed to remove document:', err);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <div className="px-6 py-4" style={{ background: committeeColor?.gradient }}>
            <DialogTitle className="text-xl text-white">Meeting Agenda</DialogTitle>
            <DialogDescription className="mt-1 text-white/80">
              Review agenda items and attach documents before the meeting starts.
            </DialogDescription>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="text-center text-gray-500 py-10">Loading agenda...</div>
            ) : agendaItems.length === 0 ? (
              <div className="text-center text-gray-500 py-10">
                <p className="mb-4">No agenda items yet.</p>
                <Button
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    onCreateAgenda?.(meetingId);
                  }}
                >
                  Create Agenda
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {agendaItems.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPickerAgendaItemId(item.id)}
                      >
                        <Paperclip className="h-3 w-3 mr-1" />
                        Attach
                      </Button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(docsByAgenda[item.id] || []).length === 0 ? (
                        <span className="text-xs text-gray-400">No documents attached</span>
                      ) : (
                        docsByAgenda[item.id].map((doc) => (
                          <span
                            key={doc.document_id}
                            className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-xs text-purple-700"
                          >
                            <Paperclip className="h-3 w-3" />
                            <span className="max-w-[180px] truncate">{doc.title}</span>
                            <button
                              type="button"
                              className="text-purple-400 hover:text-purple-600"
                              onClick={() => handleUnlinkDocument(doc.document_id)}
                            >
                              ×
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pickerAgendaItemId} onOpenChange={(open) => !open && setPickerAgendaItemId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search documents..."
              value={documentSearch}
              onChange={(e) => setDocumentSearch(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto border rounded-md">
              {documentsLoading ? (
                <div className="p-4 text-sm text-gray-500">Loading documents...</div>
              ) : availableDocuments.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No documents found</div>
              ) : (
                availableDocuments.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                    onClick={() => handleAttachDocument(doc.id, pickerAgendaItemId)}
                  >
                    <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                    <div className="text-xs text-gray-500">{doc.category || doc.file_type}</div>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PlaceholderPanel = ({ title, description }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 mb-4">{description}</p>
      <p className="text-sm text-gray-400">We will wire this to committee data soon.</p>
    </div>
  );
};

const CreateCommitteeMeetingDialog = ({ open, onOpenChange, committeeId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_type: 'regular',
    location: '',
    location_type: 'in_person',
    video_link: '',
    scheduled_start: '',
    scheduled_end: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.scheduled_start) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/governance/committees/${committeeId}/meetings`, {
        ...formData,
        scheduled_start: new Date(formData.scheduled_start).toISOString(),
        scheduled_end: formData.scheduled_end ? new Date(formData.scheduled_end).toISOString() : null
      });
      onSuccess();
      setFormData({
        title: '',
        description: '',
        meeting_type: 'regular',
        location: '',
        location_type: 'in_person',
        video_link: '',
        scheduled_start: '',
        scheduled_end: ''
      });
    } catch (err) {
      toast.error('Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule Committee Meeting</DialogTitle>
          <DialogDescription>
            Create a new committee meeting. You can add agenda items after creation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Meeting Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Marketing Committee Q1 Review"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Meeting objectives and notes..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meeting_type">Meeting Type</Label>
              <Select
                value={formData.meeting_type}
                onValueChange={(value) => setFormData({ ...formData, meeting_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_type">Location Type</Label>
              <Select
                value={formData.location_type}
                onValueChange={(value) => setFormData({ ...formData, location_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">
              {formData.location_type === 'virtual' ? 'Meeting Platform' : 'Location'}
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder={formData.location_type === 'virtual' ? 'e.g., Zoom, Google Meet' : 'e.g., Conference Room A'}
            />
          </div>

          {(formData.location_type === 'virtual' || formData.location_type === 'hybrid') && (
            <div className="space-y-2">
              <Label htmlFor="video_link">Video Link</Label>
              <Input
                id="video_link"
                type="url"
                value={formData.video_link}
                onChange={(e) => setFormData({ ...formData, video_link: e.target.value })}
                placeholder="https://..."
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled_start">Start Date & Time *</Label>
              <Input
                id="scheduled_start"
                type="datetime-local"
                value={formData.scheduled_start}
                onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scheduled_end">End Date & Time</Label>
              <Input
                id="scheduled_end"
                type="datetime-local"
                value={formData.scheduled_end}
                onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Schedule Meeting
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CreateCommitteeAgendaDialog = ({ open, onOpenChange, committeeId, committeeName, committeeColor, meetings, preSelectedMeetingId = '', onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(preSelectedMeetingId || '');
  const [mode, setMode] = useState('manual');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [aiPrompt, setAiPrompt] = useState({
    meeting_purpose: '',
    organization_type: 'corporate',
    duration_minutes: 60
  });
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [showDocumentPickerIndex, setShowDocumentPickerIndex] = useState(null);
  const [agendaItems, setAgendaItems] = useState([
    { title: '', description: '', item_type: 'discussion', duration_minutes: 15, notes: '', document_ids: [] }
  ]);

  useEffect(() => {
    if (preSelectedMeetingId) {
      setSelectedMeeting(preSelectedMeetingId);
    }
  }, [preSelectedMeetingId, open]);

  const addItem = () => {
    setAgendaItems([
      ...agendaItems,
      { title: '', description: '', item_type: 'discussion', duration_minutes: 15, notes: '', document_ids: [] }
    ]);
  };

  const removeItem = (index) => {
    if (agendaItems.length > 1) {
      setAgendaItems(agendaItems.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...agendaItems];
    updated[index][field] = value;
    setAgendaItems(updated);
  };

  const moveItem = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === agendaItems.length - 1)) return;
    const updated = [...agendaItems];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setAgendaItems(updated);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setGenerating(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      if (committeeName) {
        formData.append('committee_name', committeeName);
      }
      const response = await api.post('/board/agenda/parse-document-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.agenda_items) {
        setAgendaItems(response.data.agenda_items.map(item => ({
          ...item,
          notes: '',
          document_ids: []
        })));
        toast.success('Agenda generated from document');
      }
    } catch (err) {
      toast.error('Failed to parse document. Please try manual entry.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.meeting_purpose.trim()) {
      toast.error('Please describe the meeting purpose');
      return;
    }

    setGenerating(true);

    try {
      const response = await api.post('/board/agenda/generate', {
        meeting_type: 'regular',
        meeting_purpose: aiPrompt.meeting_purpose,
        organization_type: aiPrompt.organization_type,
        duration_minutes: aiPrompt.duration_minutes,
        committee_name: committeeName || null,
        include_standard_items: true
      });

      if (response.data.success && response.data.agenda_items) {
        setAgendaItems(response.data.agenda_items.map(item => ({
          ...item,
          notes: '',
          document_ids: []
        })));
        toast.success('Agenda generated successfully');
      }
    } catch (err) {
      toast.error('AI generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRefinement = async () => {
    if (!refinementPrompt.trim() || agendaItems.length === 0) return;

    setRefining(true);

    try {
      const response = await api.post('/board/agenda/refine', {
        current_agenda: agendaItems,
        refinement_request: refinementPrompt,
        committee_name: committeeName || null
      });

      if (response.data.success && response.data.agenda_items) {
        setAgendaItems(response.data.agenda_items.map(item => ({
          ...item,
          notes: item.notes || '',
          document_ids: item.document_ids || []
        })));
        setRefinementPrompt('');
        toast.success('Agenda refined');
      }
    } catch (err) {
      toast.error('Refinement failed. Please try again.');
    } finally {
      setRefining(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMeeting) {
      toast.error('Please select a meeting');
      return;
    }

    const validItems = agendaItems.filter(item => item.title.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one agenda item');
      return;
    }

    try {
      setLoading(true);
      for (let i = 0; i < validItems.length; i++) {
        const response = await api.post(`/governance/committees/${committeeId}/meetings/${selectedMeeting}/agenda`, {
          title: validItems[i].title,
          description: validItems[i].description,
          item_type: validItems[i].item_type,
          duration_minutes: validItems[i].duration_minutes,
          notes: validItems[i].notes,
          sort_order: i + 1
        });
        const itemId = response?.data?.item_id;
        if (itemId && validItems[i].document_ids?.length) {
          for (const docId of validItems[i].document_ids) {
            await api.post(`/governance/committees/${committeeId}/meetings/${selectedMeeting}/documents`, {
              document_id: docId,
              agenda_item_id: itemId
            });
          }
        }
      }
      onSuccess();
      setSelectedMeeting(preSelectedMeetingId || '');
      setAgendaItems([{ title: '', description: '', item_type: 'discussion', duration_minutes: 15, notes: '', document_ids: [] }]);
      setUploadedFile(null);
      setMode('manual');
      setAiPrompt({ meeting_purpose: '', organization_type: 'corporate', duration_minutes: 60 });
    } catch (err) {
      toast.error('Failed to create agenda');
    } finally {
      setLoading(false);
    }
  };

  const formatMeetingOption = (meeting) => {
    const date = new Date(meeting.scheduled_start);
    return `${meeting.title} - ${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
  };

  const totalDuration = agendaItems.reduce((sum, item) => sum + (item.duration_minutes || 0), 0);

  const fetchAvailableDocuments = async (query = '') => {
    if (!selectedMeeting) return;
    try {
      setDocumentsLoading(true);
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      const response = await api.get(`/governance/committees/${committeeId}/meetings/${selectedMeeting}/documents?${params.toString()}`);
      setAvailableDocuments(response.data.available_documents || []);
    } catch (err) {
      console.error('Failed to load committee documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMeeting) {
      fetchAvailableDocuments();
    } else {
      setAvailableDocuments([]);
    }
  }, [selectedMeeting]);

  useEffect(() => {
    if (showDocumentPickerIndex === null) return;
    const term = documentSearch.trim();
    const timeout = setTimeout(() => {
      fetchAvailableDocuments(term);
    }, 200);
    return () => clearTimeout(timeout);
  }, [documentSearch, showDocumentPickerIndex]);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <div className="px-6 py-4" style={{ background: committeeColor?.gradient }}>
          <DialogTitle className="text-xl text-white">Create Agenda</DialogTitle>
          <DialogDescription className="mt-1 text-white/80">
            Build a detailed agenda for your committee meeting
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!preSelectedMeetingId && (
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Select Meeting *</Label>
                  <Select value={selectedMeeting} onValueChange={setSelectedMeeting}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Choose a meeting..." />
                    </SelectTrigger>
                    <SelectContent>
                      {meetings.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-gray-500 text-center">
                          No upcoming meetings
                        </div>
                      ) : (
                        meetings.map((meeting) => (
                          <SelectItem key={meeting.id} value={meeting.id}>
                            {formatMeetingOption(meeting)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Total Duration</Label>
                  <div className="h-11 flex items-center px-4 bg-gray-50 border rounded-md">
                    <span className="font-medium">{Math.floor(totalDuration / 60)}h {totalDuration % 60}m</span>
                    <span className="text-gray-400 ml-2">({agendaItems.filter(i => i.title.trim()).length} items)</span>
                  </div>
                </div>
              </div>
            )}

            {preSelectedMeetingId && (
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Total Duration:</span>
                  <span className="font-medium">{Math.floor(totalDuration / 60)}h {totalDuration % 60}m</span>
                  <span className="text-gray-400">({agendaItems.filter(i => i.title.trim()).length} items)</span>
                </div>
              </div>
            )}

            <div
              className="flex items-center gap-2 p-4 rounded-lg border"
              style={{ background: committeeColor?.soft, borderColor: committeeColor?.soft }}
            >
              <button
                type="button"
                onClick={() => setMode('manual')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all text-sm ${
                  mode === 'manual'
                    ? 'bg-white shadow-sm border'
                    : 'text-gray-600 hover:bg-white/50'
                }`}
                style={mode === 'manual' ? { color: committeeColor?.accent, borderColor: committeeColor?.soft } : undefined}
              >
                Build Manually
              </button>
              <button
                type="button"
                onClick={() => setMode('upload')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all text-sm ${
                  mode === 'upload'
                    ? 'bg-white shadow-sm border'
                    : 'text-gray-600 hover:bg-white/50'
                }`}
                style={mode === 'upload' ? { color: committeeColor?.accent, borderColor: committeeColor?.soft } : undefined}
              >
                Upload Document
              </button>
              <button
                type="button"
                onClick={() => setMode('ai')}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all text-sm ${
                  mode === 'ai'
                    ? 'bg-white shadow-sm border'
                    : 'text-gray-600 hover:bg-white/50'
                }`}
                style={mode === 'ai' ? { color: committeeColor?.accent, borderColor: committeeColor?.soft } : undefined}
              >
                AI Generate
              </button>
            </div>

            {mode === 'upload' && (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileUpload} />
                {generating && <div className="mt-2 text-sm text-gray-500">Processing...</div>}
              </div>
            )}

            {mode === 'ai' && (
              <div className="space-y-4">
                <div>
                  <Label>Meeting Purpose *</Label>
                  <Textarea
                    value={aiPrompt.meeting_purpose}
                    onChange={(e) => setAiPrompt({ ...aiPrompt, meeting_purpose: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Organization Type</Label>
                    <Select
                      value={aiPrompt.organization_type}
                      onValueChange={(value) => setAiPrompt({ ...aiPrompt, organization_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corporate">Corporate</SelectItem>
                        <SelectItem value="nonprofit">Nonprofit</SelectItem>
                        <SelectItem value="startup">Startup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={aiPrompt.duration_minutes}
                      onChange={(e) => setAiPrompt({ ...aiPrompt, duration_minutes: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                </div>
                <Button type="button" onClick={handleAIGenerate} disabled={generating}>
                  Generate Agenda
                </Button>
              </div>
            )}

            <div className="space-y-3">
              {agendaItems.map((item, index) => (
                <div key={`agenda-${index}`} className="border rounded-lg p-4 space-y-3 bg-white">
                  <div className="flex items-center gap-2">
                    <Input
                      value={item.title}
                      onChange={(e) => updateItem(index, 'title', e.target.value)}
                      placeholder="Agenda item title"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => moveItem(index, -1)} disabled={index === 0}>↑</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => moveItem(index, 1)} disabled={index === agendaItems.length - 1}>↓</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} disabled={agendaItems.length === 1}>Remove</Button>
                  </div>
                  <Textarea
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="Description"
                    rows={2}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={item.item_type}
                      onValueChange={(value) => updateItem(index, 'item_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="discussion">Discussion</SelectItem>
                        <SelectItem value="presentation">Presentation</SelectItem>
                        <SelectItem value="report">Report</SelectItem>
                        <SelectItem value="break">Break</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="1"
                      value={item.duration_minutes}
                      onChange={(e) => updateItem(index, 'duration_minutes', parseInt(e.target.value) || 0)}
                      placeholder="Minutes"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {(item.document_ids || []).map((docId) => {
                      const doc = availableDocuments.find((d) => d.id === docId);
                      const title = doc?.title || 'Document';
                      return (
                        <span
                          key={`${docId}-${index}`}
                          className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-xs text-purple-700"
                        >
                          <Paperclip className="h-3 w-3" />
                          <span className="max-w-[160px] truncate">{title}</span>
                          <button
                            type="button"
                            className="text-purple-400 hover:text-purple-600"
                            onClick={() => {
                              const next = (item.document_ids || []).filter((id) => id !== docId);
                              updateItem(index, 'document_ids', next);
                            }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!selectedMeeting) {
                          toast.error('Please select a meeting first');
                          return;
                        }
                        setShowDocumentPickerIndex(index);
                      }}
                    >
                      <Paperclip className="h-3 w-3 mr-1" />
                      Attach documents
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addItem}>
                Add Agenda Item
              </Button>
            </div>

            {mode === 'ai' && (
              <div className="space-y-2">
                <Label>Refine Agenda</Label>
                <Textarea
                  value={refinementPrompt}
                  onChange={(e) => setRefinementPrompt(e.target.value)}
                  placeholder="e.g., add time for Q&A, remove budget review"
                  rows={2}
                />
                <Button type="button" variant="outline" onClick={handleRefinement} disabled={refining}>
                  Refine Agenda
                </Button>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Agenda
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={showDocumentPickerIndex !== null} onOpenChange={(open) => !open && setShowDocumentPickerIndex(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Select Documents</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Search documents..."
            value={documentSearch}
            onChange={(e) => setDocumentSearch(e.target.value)}
          />
          <div className="max-h-72 overflow-y-auto border rounded-md">
            {documentsLoading ? (
              <div className="p-4 text-sm text-gray-500">Loading documents...</div>
            ) : availableDocuments.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No documents found</div>
            ) : (
              availableDocuments.map((doc) => {
                const current = agendaItems[showDocumentPickerIndex] || {};
                const selectedIds = current.document_ids || [];
                const isSelected = selectedIds.includes(doc.id);
                return (
                  <button
                    key={doc.id}
                    type="button"
                    className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`}
                    onClick={() => {
                      const nextIds = isSelected
                        ? selectedIds.filter((id) => id !== doc.id)
                        : [...selectedIds, doc.id];
                      updateItem(showDocumentPickerIndex, 'document_ids', nextIds);
                    }}
                  >
                    <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                    <div className="text-xs text-gray-500">{doc.category || doc.file_type}</div>
                  </button>
                );
              })
            )}
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={() => setShowDocumentPickerIndex(null)}>
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CommitteeDashboard;
